import React, { forwardRef, useEffect, useState } from "react";
import BaseModal from "../../../../components/Modals/Modal";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { FaPlus } from "react-icons/fa6";
import { useUpdateProjectMutation } from "../../../../redux/api/builder/projectSlice";
import {
  Box,
  FormLabel,
  HStack,
  useToast,
  Button as ChakraButton,
  Text,
  Flex,
} from "@chakra-ui/react";
import Input, { TextArea } from "../../../../components/Input";
import SuccessMessage from "../../../../components/SuccessMessage";
import SelectSearch from "../../../../components/SelectSearch";
import Button from "../../../../components/Button";
import { handleError } from "../../../../utility/helpers";
import { uploadImage } from "../../../../utility/queries";
import Upload from "../../../../components/Icons/Upload";
import { IoIosCloseCircle } from "react-icons/io";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import CalendarIcon from "../../../../components/Icons/Calendar";
import { useGetAllBuildersUnpaginatedQuery } from "../../../../redux/api/super-admin/builderSlice";
import { useCreateFundManagerProjectMutation } from "../../../../redux/api/super-admin/fundManagerSlice";

export default function CreateProjectFundManager({
  isOpen,
  onClose,
  project = null,
  fundManagerId,
}) {
  const [projectSuccess, setProjectSuccess] = useState(false);
  const [openBuilderInvite, setOpenBuilderInvite] = useState(false);
  const [image, setImage] = useState(null);
  const [imagePlaceholder, setImagePlaceholder] = useState("Upload 3D image");
  const [imageName, setImageName] = useState("");
  const [loading, setLoading] = useState(false);
  const [randomImgKey, setRandomImgKey] = useState(Math.random());
  const [builderList, setBuilderList] = useState(null);
  const [inviteData, setInviteData] = useState(null);

  const imageHandler = async (e) => {
    if (e.target.files[0] === null) return false;
    setImage(e.target.files[0]);
    setImagePlaceholder(e.target.files[0].name);
  };

  const { data: builders } = useGetAllBuildersUnpaginatedQuery();

  useEffect(() => {
    if (builders) {
      const list = builders?.data?.map((item) => {
        return {
          ...item,
          name: item.businessName || item.email,
        };
      });
      setBuilderList(list);
    }
  }, [builders]);

  const toast = useToast();

  const createProjectSchema = yup.object({
    projectTile: yup
      .string()
      .required("Project title is required")
      .test("notEmpty", "This field cannot be empty", (value) => {
        return !/^\s*$/.test(value);
      }),
    startDate: yup.string().required("Project start date is required"),
    endDate: yup.string().required("Project end date is required"),
    projectLocation: yup
      .string()
      .required("Project location is required")
      .test("notEmpty", "This field cannot be empty", (value) => {
        return !/^\s*$/.test(value);
      }),
  });

  const inviteFundManagerSchema = yup.object({
    inviteeEmail: yup
      .string()
      .required("project sponsor  email is required")
      .email(),
  });

  const methods = useForm({
    defaultValues: {
      projectTile: "",
      projectDesc: "",
      startDate: "",
      endDate: "",
      projectLocation: "",
      selectBuilder: "",
    },
    resolver: yupResolver(createProjectSchema),
  });

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
  } = methods;

  useEffect(() => {
    if (project) {
      setValue("projectTile", project.title);
      setValue("startDate", new Date(project.startDate));
      setValue("endDate", new Date(project.endDate));
      setValue("projectLocation", project.location);
    }
  }, [project, setValue]);

  const [
    createFundManagerProject,
    {
      isLoading: createLoading,
      isSuccess: createSuccess,
      error: createError,
      isError: createIsError,
    },
  ] = useCreateFundManagerProjectMutation();

  const [
    updateProject,
    {
      isLoading: updateLoading,
      isSuccess: updateSuccess,
      error: updateError,
      isError: updateIsError,
    },
  ] = useUpdateProjectMutation();

  const {
    control: inviteControl,
    handleSubmit: handleInviteSubmit,
    formState: { errors: inviteErrors },
    reset: resetInvite,
  } = useForm({
    defaultValues: {
      inviteeEmail: "",
      message: "",
    },
    resolver: yupResolver(inviteFundManagerSchema),
  });
  const submitInvite = (data) => {
    setInviteData(data);
    setOpenBuilderInvite(false);
    resetInvite();
  };

  const submitProject = async (data) => {
    if (project?.id) {
      updateProject({
        payload: {
          location: data.projectLocation,
          startDate: new Date(data.startDate),
          endDate: new Date(data.endDate),
          title: data.projectTile,
        },
        id: project.id,
      });
    } else {
      let img;
      if (image) {
        setLoading(true);
        img = await uploadImage(image, "image", false);
        setLoading(false);
      }
      createFundManagerProject({
        id: fundManagerId,
        payload: {
          location: data.projectLocation,
          startDate: new Date(data.startDate),
          endDate: new Date(data.endDate),
          title: data.projectTile,
          description: data.projectDesc,
          fileName: imageName ? imageName : imagePlaceholder,
          image: img && img.url,
          builderId: data.selectBuilder?.id,
          message: inviteData?.message,
          inviteeEmail: inviteData?.inviteeEmail,
        },
      });
    }
  };

  useEffect(() => {
    if (createSuccess || updateSuccess) {
      setProjectSuccess(true);
    }
    if (createIsError || updateIsError) {
      toast({
        description: handleError(createError || updateError),
        status: "error",
        position: "top-right",
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createLoading, updateLoading]);

  useEffect(() => {
    if (!isOpen && project == null) {
      reset();
    }
  }, [isOpen, reset, project]);

  useEffect(() => {
    if (!openBuilderInvite) {
      setOpenBuilderInvite(false);
    }
  }, [openBuilderInvite, resetInvite]);

  const StartDate = forwardRef(({ value, onClick }, ref) => (
    <Input
      onClick={onClick}
      ref={ref}
      placeholder="Start date"
      value={value}
      readOnly
      rightIcon={<CalendarIcon />}
    />
  ));

  const EndDate = forwardRef(({ value, onClick }, ref) => (
    <Input
      onClick={onClick}
      ref={ref}
      placeholder="End date"
      value={value}
      readOnly
      rightIcon={<CalendarIcon />}
    />
  ));

  return (
    <>
      <BaseModal
        isOpen={isOpen}
        onClose={() => {
          onClose();
          setProjectSuccess(false);
          setImagePlaceholder("Upload 3D image");
          setImage(null);
        }}
        title={`${project == null ? "Created" : "Updated"} Project`}
        subtitle={`The copy here would explain what ${
          project == null ? "Creating" : "Updating"
        } a project means`}
      >
        {projectSuccess ? (
          <SuccessMessage
            message={`Project ${
              project == null ? "Created" : "Updated"
            } Successfully`}
          />
        ) : (
          <form onSubmit={handleSubmit(submitProject)}>
            <Box mb={"24px"}>
              <Controller
                control={control}
                defaultValue=""
                name="projectTile"
                render={({ field: { onChange, value } }) => (
                  <Box w={"100%"}>
                    <Input
                      placeholder="Add Project Title"
                      label="Project Title"
                      value={value}
                      onChange={onChange}
                      isRequired
                    />
                    <div style={{ color: "red" }}>
                      {errors["projectTile"]
                        ? errors["projectTile"]?.message
                        : ""}
                    </div>
                  </Box>
                )}
              />
            </Box>

            {project === null && (
              <Box mb={"24px"}>
                <Controller
                  control={control}
                  defaultValue=""
                  name="projectDesc"
                  render={({ field: { onChange, value } }) => (
                    <Box w={"100%"}>
                      <TextArea
                        label="Project Description"
                        placeholder="Add Project Description..."
                        value={value}
                        isRequired
                        onChange={onChange}
                      />
                      <div style={{ color: "red" }}>
                        {errors["projectDesc"]
                          ? errors["projectDesc"]?.message
                          : ""}
                      </div>
                    </Box>
                  )}
                />
              </Box>
            )}
            <Box mb="24px">
              <Text mb="8px" fontSize="14px" fontWeight="400">
                Project 3D Image (optional)
              </Text>
              <HStack
                justifyContent="space-between"
                spacing="16px"
                flexWrap={{ base: "wrap", md: "nowrap" }}
              >
                <Flex
                  align="center"
                  bgColor="rgba(18, 53, 90, 0.04)"
                  h="48px"
                  borderRadius="8px"
                  border="0.5px solid #999"
                  w={{ base: "100%", md: "65%" }}
                >
                  <Flex
                    as="label"
                    p="8px 16px"
                    htmlFor="uploadImage"
                    align="center"
                    cursor="pointer"
                    w={{ base: "100%", md: "80%" }}
                  >
                    <Upload />
                    <Text ml="8px" fontSize="12px">
                      {imagePlaceholder}
                    </Text>
                    <input
                      key={randomImgKey}
                      type="file"
                      id="uploadImage"
                      accept={"image/*"}
                      onChange={(e) => {
                        imageHandler(e);
                      }}
                      hidden
                    />
                  </Flex>
                  <Flex
                    align="center"
                    ml="auto"
                    pr="16px"
                    cursor="pointer"
                    onClick={() => {
                      setImage(null);
                      setImagePlaceholder("Upload 3D image");
                      setRandomImgKey(Math.random());
                    }}
                  >
                    {imagePlaceholder !== "Upload 3D image" && (
                      <IoIosCloseCircle color="#999999" />
                    )}
                  </Flex>
                </Flex>
                <Box w={{ base: "100%", md: "35%" }}>
                  <Input
                    placeholder="Enter file name"
                    value={imageName}
                    onChange={(e) => setImageName(e.target.value)}
                  />
                </Box>
              </HStack>
            </Box>

            <Box mb="24px">
              <FormLabel mb="8px" fontSize="14px" fontWeight="400">
                Project Duration <span style={{ color: "red" }}>*</span>
              </FormLabel>
              <HStack
                mb={"24px"}
                justifyContent="space-between"
                spacing="16px"
                flexWrap={{ base: "wrap", md: "nowrap" }}
              >
                <Box w="100%">
                  <Controller
                    control={control}
                    defaultValue=""
                    name="startDate"
                    render={({ field: { onChange, value } }) => (
                      <Box w={"100%"}>
                        <DatePicker
                          selected={value}
                          onChange={onChange}
                          // minDate={new Date()}
                          customInput={<StartDate />}
                          wrapperClassName="datepicker_width"
                          showMonthDropdown
                          showYearDropdown
                        />
                        <div style={{ color: "red" }}>
                          {errors["startDate"]
                            ? errors["startDate"]?.message
                            : ""}
                        </div>
                      </Box>
                    )}
                  />
                </Box>
                <Box w="100%">
                  <Controller
                    control={control}
                    defaultValue=""
                    name="endDate"
                    render={({ field: { onChange, value } }) => (
                      <Box w={"100%"}>
                        <DatePicker
                          selected={value}
                          // minDate={new Date()}
                          onChange={onChange}
                          customInput={<EndDate />}
                          wrapperClassName="datepicker_width"
                          showMonthDropdown
                          showYearDropdown
                        />
                        <div style={{ color: "red" }}>
                          {errors["endDate"] ? errors["endDate"]?.message : ""}
                        </div>
                      </Box>
                    )}
                  />
                </Box>
              </HStack>
            </Box>

            <Box mb={"24px"}>
              <Controller
                control={control}
                defaultValue=""
                name="projectLocation"
                render={({ field: { onChange, value } }) => (
                  <Box w={"100%"}>
                    <Input
                      placeholder="Enter Project Location"
                      value={value}
                      onChange={onChange}
                      label="Project Location"
                      isRequired
                    />
                    <div style={{ color: "red" }}>
                      {errors["projectLocation"]
                        ? errors["projectLocation"]?.message
                        : ""}
                    </div>
                  </Box>
                )}
              />
            </Box>

            <Box mb={"24px"}>
              <FormLabel mb="8px" fontSize="14px" fontWeight="400">
                Project Contractor <span style={{ color: "red" }}>*</span>
              </FormLabel>
              <Controller
                control={control}
                defaultValue=""
                name="selectBuilder"
                render={({ field: { onChange, value } }) => (
                  <Box w={"100%"}>
                    <SelectSearch
                      placeholder="Select Project Contractor"
                      data={builderList}
                      setSelectOption={onChange}
                      selectOption={value}
                      showInvite={true}
                      inviteText="Add Contractor"
                      setOpenInvite={setOpenBuilderInvite}
                    />
                  </Box>
                )}
              />
            </Box>

            <Box mt="41px">
              <Button full isSubmit isLoading={createLoading}>
                Create Project
              </Button>
            </Box>
          </form>
        )}
      </BaseModal>
      <BaseModal
        isOpen={openBuilderInvite}
        onClose={() => setOpenBuilderInvite(false)}
        title="Add Contractor"
        subtitle="Add a contractor to undertake this project"
      >
        <form onSubmit={handleInviteSubmit(submitInvite)}>
          <Box mb={"24px"}>
            <Controller
              control={inviteControl}
              defaultValue=""
              name="inviteeEmail"
              render={({ field: { onChange, value } }) => (
                <Box w={"100%"}>
                  <Input
                    placeholder="mail@mail.com"
                    label="Email Address"
                    value={value}
                    onChange={onChange}
                    isRequired
                  />
                  <div style={{ color: "red" }}>
                    {inviteErrors["inviteeEmail"]
                      ? inviteErrors["inviteeEmail"]?.message
                      : ""}
                  </div>
                </Box>
              )}
            />
          </Box>

          <Box mb={"24px"}>
            <Controller
              control={inviteControl}
              defaultValue=""
              name="message"
              render={({ field: { onChange, value } }) => (
                <Box w={"100%"}>
                  <TextArea
                    label="Message"
                    placeholder="Add a default message here (It can be edited)"
                    value={value}
                    onChange={onChange}
                    h="189px"
                  />
                </Box>
              )}
            />
          </Box>

          <Box mt="41px">
            <Button full isSubmit>
              Send Invite
            </Button>
          </Box>
        </form>
      </BaseModal>
    </>
  );
}
